import type { A2UIMessage } from '@atlassian/a2ui-renderer/types/A2UIProtocol.types';
import {
	type AnswerGenerationType,
	type ConversationChannelMessageStatus,
	type ConversationChannelScope,
	type ConversationChannelStatus,
	type HumanMessage,
	type MessageType,
	type StreamDynamicLayoutSubAgent,
	type StreamFollowUp,
	type StreamResponse,
	type TodoItem,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type MessageAction,
	type MessageActionInvocationStatus,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { type StoreAbortController } from '../../common/utils/abort-controller/types';
import { type StoreError } from '../chat-signals';

export enum StoreActionLoggableName {
	addStoreChatItem = 'addStoreChatItem',
	addHumanMessage = 'addHumanMessage',
	addAssistantMessage = 'addAssistantMessage',
	clearFollowUpMessages = 'clearFollowUpMessages',
	sendMessage = 'sendMessage',
	sendMessageConfirmedTools = 'sendMessageConfirmedTools',
	removeMessage = 'removeMessage',
	truncateFromLastHumanMessage = 'truncateFromLastHumanMessage',
	fetchConversation = 'fetchConversation',
	prefetchConversationMessages = 'prefetchConversationMessages',
	reconnectToInProgressStream = 'reconnectToInProgressStream',
	abortRequests = 'abortRequests',
	answerNow = 'answerNow',
	addErrorMessage = 'addErrorMessage',
	fetchConversationChannelsData = 'fetchConversationChannelsData',
	loadMoreConversationChannelsData = 'loadMoreConversationChannelsData',
	hydrateConversation = 'hydrateConversation',
	addAgentMessage = 'addAgentMessage',
	handleChatOpenPubsubAction = 'handleChatOpenPubsubAction',
	createConversationOptimistically = 'createConversationOptimistically',
	createSeededConversation = 'createSeededConversation',
	updateActionInvocationStatus = 'updateActionInvocationStatus',
	updateActionData = 'updateActionData',
	resolveConversationAction = 'resolveConversationAction',
	bulkResolveConversationActions = 'bulkResolveConversationActions',
	bulkResolveConversationUIActions = 'bulkResolveConversationUIActions',
	setSelectedConversation = 'setSelectedConversation',
	warmConversationCache = 'warmConversationCache',
	hasConversationMessages = 'hasConversationMessages',
	stopTask = 'stopTask',
}

export type ChatStorePrivateState = {
	conversations: {
		[conversationId: string]: Conversation;
	};
	currentConversationId: string;
	liveChatMetadata?: {
		remoteEntityId: string;
		conversationAri?: string;
	} | null;
	isConversationsInitialized?: boolean;
	conversationPagination: {
		nextCursor: string | null;
		hasMore: boolean;
		isLoadingMore: boolean;
		loadMoreError: StoreError | null;
		isConversationListMounted: boolean;
	};
};

export const DEFAULT_CONVERSATION_PAGINATION: ChatStorePrivateState['conversationPagination'] = {
	nextCursor: null,
	hasMore: false,
	isLoadingMore: false,
	loadMoreError: null,
	isConversationListMounted: false,
};

/**
 * Metadata for a single attachment uploaded via the CSM Twilio live chat
 * (Media Content Service / MCS). Stored alongside the message so we can
 * re-resolve the temporary download URL at render time without re-fetching
 * the entire Twilio `Message` object.
 *
 * `mediaSid` is the Twilio MCS sid. Per Twilio's docs, temporary download
 * URLs returned by `Message.getTemporaryContentUrlsForMediaSids` (or the
 * per-media `Media.getContentTemporaryUrl`) are valid for **300 seconds**
 * and **must not be cached** — re-resolve on demand at render/click time.
 *
 * The fields below mirror Twilio's `Media` shape (`sid`, `contentType`,
 * `filename`, `size`); `filename` is nullable because the SDK declares
 * `Media.filename: string | null`. Once created, Twilio media files are
 * **immutable** and are deleted automatically when their owning message
 * is deleted — we rely on that lifecycle and do not attempt to mutate
 * media independently of its message.
 *
 * Gated on the FE behind the `csm-omni-channel-live-chat-attachments`
 * feature gate.
 */
export type CsmTwilioAttachment = {
	mediaSid: string;
	contentType: string;
	filename: string | null;
	size: number;
};

export type StoreHuman = {
	type: 'HUMAN';
	content: HumanMessage;
	source?: 'TWILIO_LIVE_CHAT';
	/** Set when a PROMPT_REDACTION stream message is received for this human message. */
	redactedPrompt?: string;
	/**
	 * CSM Twilio live chat attachments authored by the help-seeker.
	 * Only populated when the message was sent through the Twilio
	 * SDK with one or more files attached, and the
	 * `csm-omni-channel-live-chat-attachments` feature gate is on.
	 */
	csmTwilioAttachments?: CsmTwilioAttachment[];
	/**
	 * Indicates whether this message was loaded from historical conversation data
	 * (via fetchConversation) vs being sent by the user in real-time.
	 * Set to true when loading conversation history, undefined/false for new messages.
	 */
	isHistorical?: boolean;
};

/**
 * Status of the Answer Now feature for fast-forwarding extended thinking.
 * - AVAILABLE: Fast mode response is available, user can click "Answer Now"
 * - SWITCHING: User clicked "Answer Now", waiting for fast mode to be enabled
 */
/**
 * Answer Now status types - discriminated union where AVAILABLE holds both IDs:
 * - tmpMessageId: used for store operations (finding/updating the message)
 * - remoteMessageId: the BE-provided agentMessageId for the API call
 */
export type AnswerNowStatusAvailable = {
	type: 'AVAILABLE';
	tmpMessageId: string;
	remoteMessageId: string;
};
export type AnswerNowStatusOther = {
	type: 'SWITCHING' | 'SWITCHING_REQUESTED' | 'SWITCHED' | 'ERRORED';
};
export type AnswerNowStatus = AnswerNowStatusAvailable | AnswerNowStatusOther;

/** Helper to check if status is AVAILABLE and extract message IDs */
export const isAnswerNowAvailable = (
	status?: AnswerNowStatus,
): status is AnswerNowStatusAvailable => status?.type === 'AVAILABLE';

/** Answer Now status type constants for easier usage */
export const AnswerNowStatusType = {
	AVAILABLE: 'AVAILABLE',
	SWITCHING: 'SWITCHING',
	SWITCHING_REQUESTED: 'SWITCHING_REQUESTED',
	SWITCHED: 'SWITCHED',
	ERRORED: 'ERRORED',
} as const;

export type StoreAssistant = {
	type: 'ASSISTANT';
	content: StreamResponse;
	isAborted?: boolean;
	humanMessageId?: string;
	/** Set when a RESPONSE_REDACTION stream message is received for this assistant message. */
	redactedResponse?: string;

	// Track action invocations against an Agent message
	invokedActions?: {
		[invocationId: string]: MessageActionInvocationStatus;
	};

	markdownData?: Record<string, string>;
	answerGenerationType?: AnswerGenerationType;
	dynamicLayoutSubAgentMessages?: StreamDynamicLayoutSubAgent[];

	/**
	 * Status of the Answer Now feature for this message.
	 * Only set during streaming when extended thinking is active.
	 */
	answerNowStatus?: AnswerNowStatus;
	memory?: StoreMemoryUpdate;
	/**
	 * Accumulated agentic UI protocol messages streamed from the backend.
	 */
	agenticUiMessages?: A2UIMessage[];

	/**
	 * Indicates whether this message was loaded from historical conversation data
	 * (via fetchConversation) vs being streamed in real-time.
	 * Set to true when loading conversation history, undefined/false for new messages.
	 */
	isHistorical?: boolean;
};

export type StoreFollowUp = {
	type: 'FOLLOW_UP';
	content: StreamFollowUp;
	id: number;
	agentMessageId?: string;
	humanMessageId?: string;
};

export type StoreLiveChat = {
	type: 'LIVE_CHAT';
	id: string;
	content: string;
	author?: string;
	timestamp?: string;
	eventType:
		| 'message'
		| 'participant_joined'
		| 'participant_left'
		| 'chat_started'
		| 'system_message'
		| 'chat_closed'
		| 'subscription_error'
		| 'send_message_error';
	liveChatConversationId: string;
};

export type StoreMessage = StoreHuman | StoreAssistant;

export type StoreTwilioLiveChat = {
	type: 'TWILIO_LIVE_CHAT_AGENT';
	sid: string;
	content: string;
	author: string | null;
	dateCreated: Date | null;
	conversationSid: string;
	attributes?: { event?: string; twilioUsername?: string };
	/**
	 * Attachments authored by the agent and surfaced to the help-seeker.
	 * Only populated when the agent sent a message with one or more files
	 * attached via Twilio MCS, and the
	 * `csm-omni-channel-live-chat-attachments` feature gate is on.
	 */
	csmTwilioAttachments?: CsmTwilioAttachment[];
};

export type StoreMemoryUpdate = {
	type: 'MEMORY_UPDATE';
	content?: string;
};

export type StorePhotoCardPreview = {
	type: 'PHOTO_CARD_PREVIEW';
	content?: string;
};

export type StoreActionPrepare = {
	type: 'ACTION_PREPARE';
	content?: string;
	action?: MessageAction;
};

export type StoreActionComplete = {
	type: 'ACTION_COMPLETE';
	content?: string;
	action?: MessageAction;
};

export type StoreGuardBlock = {
	type: 'GUARD_BLOCK';
	id: string;
	time_created: string;
	traceId?: string;
	blockType?: 'PROMPT_BLOCKED' | 'RESPONSE_BLOCKED' | 'ATTACHMENT_BLOCKED';
};

// Represents an invisible marker in the chat that other parts of the UI can react to.
// This can be used to 'hint' to the user that they need to revise their query or take action somehow.
export type StoreUsageHint = {
	type: 'USAGE_HINT';
	topic: string;
	variant?: string;
};

export type StoreChatItem =
	| StoreMessage
	| StoreError
	| StoreFollowUp
	| StoreLiveChat
	| StoreTwilioLiveChat
	| StorePhotoCardPreview
	| StoreMemoryUpdate
	| StoreActionPrepare
	| StoreActionComplete
	| StoreGuardBlock
	| StoreUsageHint;

export enum CHAT_STORE_STATUS {
	idle,
	loadingChat,
	awaitingResponse,
	typingResponse,
	deactivatedAgentError,
}

export type MessageTemplate =
	| 'ANALYSING_QUERY'
	| 'CONTENT_SEARCH'
	| 'RESEARCH'
	| 'RESEARCH_STEP_SOURCE'
	| 'RESEARCH_STEP_COMPLETE'
	| 'RESEARCH_STEP_TITLE'
	| 'RESEARCH_STEP_TITLE_SHORT'
	| 'RESEARCH_STEP'
	| 'RESEARCH_START'
	| 'SKILL_START'
	| 'SKILL_COMPLETE'
	| 'PAGE_HYDRATION'
	| 'WRITING'
	| 'ERROR'
	| 'ACCEPTABLE_USE_VIOLATIONS'
	| 'MEMORY_UPDATE'
	| 'MEMORY_SEARCH'
	| 'EXECUTING_PLUGIN'
	| 'PLUGIN_INVOCATION_INFO'
	| 'AGENT_EXECUTE'
	| 'MCP_UNAUTHORIZED'
	| 'THINK_DEEPER_TRIGGERED';

export type Trace = {
	messageTemplate?: MessageTemplate;
	messageContent: string;
	// TODO: sync type with platform/packages/ai-mate/conversation-assistant-ui-components/src/common/ui/loading-status-indicator/index.tsx
	status?: 'loading' | 'success' | 'error';
};

/**
 * Agent version info stored on conversations for display purposes.
 * Includes versionNumber for UI rendering (e.g. "V1" lozenge).
 */
export type AgentVersion = {
	versionId?: string;
	versionType?: string;
	versionNumber?: number;
};

export type Conversation = {
	id: string;
	name: string;
	/** @private
	 * @deprecated use `useChatMessages` hook instead
	 *
	 * If you are inside `/ai-mate/conversation-assistant/` DO NOT USE THIS FIELD, it will always be empty.
	 */
	messages: StoreChatItem[];
	status: CHAT_STORE_STATUS;
	traces: Trace[];
	storeAbortController?: StoreAbortController;
	timeCreated: string;
	sourceId?: string;
	lastMessageTimestamp?: string;
	lastMessage?: string;
	lastMessageType?: MessageType[];
	lastAgentMessageStatus?: ConversationChannelMessageStatus;
	sourceAgentId?: string;
	agentVersion?: AgentVersion;
	channelStatus?: ConversationChannelStatus;
	agent?: AgentMetadata;
	errorCode?: string;
	readStatus?: boolean | null;
	scopes?: ConversationChannelScope[];
	tags?: string[];
	/** When space id is set, conversation is hidden from the global chat history list. */
	rovoSpaceId?: string;
	/**
	 * The real backend-assigned conversation ID, populated when the FINAL_RESPONSE
	 * stream message arrives. The store key (`id`) is the client-generated optimistic
	 * UUID that does NOT match the backend ID. Used to dedupe between optimistic
	 * (client UUID) and API-fetched (backend ID) entries when rendering navigation lists.
	 */
	backendConversationId?: string;
	skipNextFetch?: boolean;
	/**
	 * When set, ChatHistory will scroll to this message after the conversation loads.
	 * Set by Go to Chat from the Tasks List. Cleared after use.
	 */
	targetTaskMessageId?: string | null;
	/**
	 * Agent IDs represented by @-mention chips in the current chat input draft.
	 *
	 * This is next-message routing state: when the user submits through a chat-input-originated send, the active chip decides which agent receives that turn.
	 * It is distinct from:
	 *   - `agent`: backend/optimistic metadata for the agent the conversation is
	 *     associated with, used by the header, selector, and fullscreen handoff.
	 *   - `sourceAgentId`: the agent id that launched or started the conversation,
	 *     kept for older bootstrap paths and analytics/state compatibility.
	 *
	 * The array is append-only and duplicate-preserving. Each entry corresponds
	 * to one mention chip in the chat input. The **last entry** is the active chip;
	 * earlier entries are shown disabled in the chat input. Empty array means there
	 * is no selected-agent override for the next message.
	 *
	 * `addSelectedAgentId` appends with no dedup. `removeSelectedAgentId`
	 * removes the rightmost occurrence when its chip is deleted. `setSelectedAgentIds`
	 * replaces the whole list for bulk synchronization with chat input/channel state.
	 *
	 * Reads should go through the consuming chat store hooks (`useActiveSelectedAgentId`
	 * for the active chip, `useSelectedAgentIdsHistory` for the full history).
	 * Never read `selectedAgentIds[0]` directly — `[0]` is the oldest entry, not
	 * the active one.
	 */
	selectedAgentIds: string[];
};

export type TaskProgressState =
	| 'Draft'
	| 'Queued'
	| 'Running'
	| 'AwaitingDisambiguation'
	| 'AwaitingPlanConfirmation'
	| 'AwaitingActionConfirmation'
	| 'Completed'
	| 'Failed'
	| 'Cancelled';

const TERMINAL_TASK_STATES: TaskProgressState[] = ['Completed', 'Failed', 'Cancelled'];

export const isTerminalTaskState = (state: TaskProgressState): boolean =>
	TERMINAL_TASK_STATES.includes(state);

export type TodoSnapshot = {
	todos: TodoItem[];
};

export type ExecutionSegment = {
	startedAt: string;
	endedAt?: string;
};

export type AgentMetadata = {
	agent_id: string;
	agent_named_id: string;
	agent_name: string;
	identity_account_id?: string;
	creator_type: string;
	forge_url?: string;
	is_default?: boolean;
};

export const isStoreHuman = (message: StoreChatItem): message is StoreHuman =>
	'type' in message && message.type === 'HUMAN';

export const isStoreAssistant = (message: StoreChatItem): message is StoreAssistant =>
	'type' in message && message.type === 'ASSISTANT';

export const isStoreError = (message: StoreChatItem): message is StoreError =>
	'type' in message && message.type === 'ERROR';

export const isStoreMessage = (message: StoreChatItem): message is StoreMessage =>
	isStoreHuman(message) || isStoreAssistant(message);

export const isStoreMemoryUpdate = (message: StoreChatItem): message is StoreMemoryUpdate =>
	'type' in message && message.type === 'MEMORY_UPDATE';

export const isStoreFollowUp = (message: StoreChatItem): message is StoreFollowUp =>
	'type' in message && message.type === 'FOLLOW_UP';

export const isStoreLiveChat = (message: StoreChatItem): message is StoreLiveChat =>
	'type' in message && message.type === 'LIVE_CHAT';

export const isStoreTwilioLiveChatAgent = (
	message: StoreChatItem,
): message is StoreTwilioLiveChat => 'type' in message && message.type === 'TWILIO_LIVE_CHAT_AGENT';

export const isStoreTwilioLiveChatHuman = (
	message: StoreChatItem,
): message is StoreHuman & { source: 'TWILIO_LIVE_CHAT' } =>
	isStoreHuman(message) && message.source === 'TWILIO_LIVE_CHAT';

export const isStoreTwilioLiveChatMessage = (message: StoreChatItem): boolean =>
	isStoreTwilioLiveChatAgent(message) || isStoreTwilioLiveChatHuman(message);

export const isStoreActionPrepare = (message: StoreChatItem): message is StoreActionPrepare =>
	message.type === 'ACTION_PREPARE';

export const isStoreGuardBlock = (message: StoreChatItem): message is StoreGuardBlock =>
	message.type === 'GUARD_BLOCK';

export const isStoreUsageHint = (message: StoreChatItem): message is StoreUsageHint =>
	'type' in message && message.type === 'USAGE_HINT';

export const getActionInvocationStatus = (
	invocationId: string,
	invokedActions: StoreAssistant['invokedActions'],
): MessageActionInvocationStatus | 'idle' =>
	(invokedActions && invokedActions[invocationId]) || 'idle';
